import React, { useEffect, useMemo, useState } from 'react';

import { DownloadIcon } from '@vlabs/icons';
import { ClassifyDeepfakeButton } from '@vlabs/monokit/features/classify-deepfake';
import { cutBboxFromImage } from '@vlabs/monokit/shared/lib/cut-bbox-from-image';
import { estimateLivenessDeepfake } from '@vlabs/shared/requests';
import { selectAppService } from '@vlabs/shared/selectors/appSelectors';
import { downloadJson } from '@vlabs/shared/utils';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { selectDynamicHandlerId } from '@vlabs/pages/handlers/selectors';

import type { SectionProps } from '../types';
import st from './DeepfakeSection.module.sass';
import { ResultSectionPanel } from '../../../../components/result-section-panel/ResultSectionPanel';
import { useChecksResultContext } from '../../checksResultContext';
import type { TFormType } from '../../image-check-form/types';
import { BBOX_STYLES } from '../../image-checks-processing-image/constants';
import { Loader } from '../../loader/Loader';
import { EstimationError } from '../estimation-error';
import { fileToBase64 } from '../helpers';
import { DeepfakeResultItem, FaceQualityResultItem } from '../result-items';
import { DeepfakeFaceResultItem } from '../result-items/DeepfakeFaceResultItem';
import { ImageModificationResultItem } from '../result-items/ImageModificationResultItem';

function DeepfakeSection({
  file,
}: SectionProps): React.JSX.Element | undefined {
  const { t } = useTranslation();
  const { search } = useLocation();
  const [base64Image, setBase64Image] = useState();
  const checkType: TFormType = useMemo(() => (qs.parse(search)?.type ?? 'bank') as TFormType, [search]);
  // FIXME как будто нужен норм тип для results
  const [results, setResults] = useState<object | object[]>();
  const [resultsJson, setResultsJson] = useState<object>();
  const [error, setError] = useState<string>();
  const { updateCheckResult, setBboxList } = useChecksResultContext();

  const dynamicHandlerId = useSelector<unknown, string>(selectDynamicHandlerId);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  const lpVersion = useSelector((store) => selectAppService(store, 'lunaPlatform')?.versionNum);

  useEffect(() => {
    fileToBase64(file).then(setBase64Image);
  }, [file]);

  useEffect(() => {
    if (!dynamicHandlerId) return;
    estimateLivenessDeepfake({
      file,
      handlerId: dynamicHandlerId,
      lpVersion,
      deepfake: true,
      raw: true,
      multifacePolicy: checkType === 'content' ? 1 : undefined,
      withImageModification: checkType === 'content' ? false : undefined,
      withFaceQuality: checkType === 'content' ? false : undefined,
    }).then((resp) => {
      setResultsJson(resp);

      if ((resp?.events?.length ?? 0) === 0) {
        setError(t('checks:error.no events'));
        return;
      }

      if (checkType === 'bank') {
        setResults(resp?.events?.[0]?.detections?.[0]?.samples?.face?.detection);
      } else {
        setResults(resp?.events?.map((event) => event?.detections?.[0]?.samples?.face?.detection));
      }
    }).catch(($error) => {
      if ($error.error_code !== 11038) throw $error;
      setError(t('checks:error.multiple faces') as unknown as string);
    });
  }, [lpVersion, dynamicHandlerId, file, t, checkType]);

  useEffect(() => {
    if (!results) return;
    updateCheckResult?.(
      results?.attributes?.deepfake?.prediction === 'real'
        && results?.face_quality?.status === 1,
    );
    if (checkType === 'content') {
      setBboxList?.(
        (results as object[]).map((detection) => {
          return ({
            ...detection.rect,
            style: BBOX_STYLES[String(detection?.attributes?.deepfake?.prediction === 'real')],
          });
        }),
      );
    }
  }, [results, updateCheckResult, checkType, setBboxList]);

  const enhancedDetections = useMemo(() => {
    if (!results || !base64Image) return undefined;
    if (checkType === 'bank') {
      return {
        ...(results as object),
        frames: [{
          image: base64Image,
          bbox: results.rect,
        }],
      };
    }
    return (results as object[]).map((result) => ({
      ...result,
      face: cutBboxFromImage({
        image: file,
        bbox: result.rect,
      }),
      frames: [{
        image: base64Image,
        bbox: result.rect,
      }],
    })) as object[];
  }, [file, results, base64Image, checkType]);

  const imageModificationResult = results?.face_quality?.checks?.find(({ name }) => name === 'image_modification').result;
  const faceQualityChecks: [] = results?.face_quality?.checks?.filter(({ name }) => name !== 'image_modification');
  const faceQualityChecksStatus = faceQualityChecks?.reduce((acc, { result }) => acc && result, 1);

  if (checkType === 'content') {
    return (
      <React.Fragment>
        <IconButton
          disabled={!resultsJson}
          icon={<DownloadIcon />}
          kind="secondary"
          onClick={() => downloadJson(`${file.name}_deepfake`, resultsJson)}
          shape="round"
          size="small"
          style={{
            position: 'absolute',
            top: '17px',
            right: '20px',
          }}
          variant="dimmed"
        />
        <EstimationError error={error} />
        {(enhancedDetections as object[])?.map((detection) => (
          <React.Fragment key={JSON.stringify(detection)}>
            <DeepfakeFaceResultItem
              face={detection.face}
              frames={detection.frames}
              prediction={detection.attributes.deepfake.prediction}
              score={detection.attributes.deepfake.score}
            />
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  }
  if (checkType === 'bank') {
    return (
      <ResultSectionPanel
        subtitle={(
          results ? (
            <div className={st.Subtitle}>
              <IconButton
                disabled={!resultsJson}
                icon={<DownloadIcon />}
                kind="secondary"
                onClick={() => downloadJson(`${file.name}_deepfake`, resultsJson)}
                shape="round"
                size="small"
                variant="dimmed"
              />
            </div>
          ) : undefined
        )}
        title={t('checks:estimations.deepfake.titleWithImageModification') as unknown as string}
      >
        <EstimationError error={error} />
        {!!enhancedDetections && (
          <React.Fragment>
            <DeepfakeResultItem
              prediction={enhancedDetections.attributes.deepfake.prediction}
              score={enhancedDetections.attributes.deepfake.score}
            />
            <ClassifyDeepfakeButton
              disabled={enhancedDetections.attributes.deepfake.prediction !== 'fake'}
              frames={enhancedDetections.frames}
            />
            <ImageModificationResultItem
              result={imageModificationResult}
            />
            <FaceQualityResultItem
              checks={faceQualityChecks}
              status={faceQualityChecksStatus}
            />
          </React.Fragment>
        )}
        {!results && !error && <Loader />}
      </ResultSectionPanel>
    );
  }
}

export { DeepfakeSection };
